import { useCallback, useRef } from 'react';

interface RequestCache {
  [key: string]: {
    data: unknown;
    timestamp: number;
    promise?: Promise<unknown>;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const requestCache: RequestCache = {};

export const useSupabaseOptimized = () => {
  const pendingRequests = useRef<Map<string, Promise<unknown>>>(new Map());

  const makeRequest = useCallback(async <T>(
    key: string,
    requestFn: () => PromiseLike<{ data: T | null; error: unknown }>,
    options?: {
      cache?: boolean;
      forceRefresh?: boolean;
    }
  ): Promise<{ data: T | null; error: unknown }> => {
    const cacheKey = key;
    const now = Date.now();

    // Verificar se já existe uma requisição pendente
    if (pendingRequests.current.has(cacheKey)) {
      if (import.meta.env.DEV) console.log(`Reutilizando requisição pendente para: ${cacheKey}`);
      const pendingPromise = pendingRequests.current.get(cacheKey) as Promise<{ data: T | null; error: unknown }>;
      return pendingPromise;
    }

    // Verificar cache se habilitado
    if (options?.cache && !options?.forceRefresh && requestCache[cacheKey]) {
      const cached = requestCache[cacheKey];
      if (now - cached.timestamp < CACHE_DURATION) {
        if (import.meta.env.DEV) console.log(`Retornando dados do cache para: ${cacheKey}`);
        return { data: cached.data as T, error: null };
      }
    }

    // Fazer a requisição
    if (import.meta.env.DEV) console.log(`Fazendo requisição para: ${cacheKey}`);
    const requestPromise = Promise.resolve(requestFn());
    pendingRequests.current.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Salvar no cache se habilitado
      if (options?.cache && result.data) {
        requestCache[cacheKey] = {
          data: result.data,
          timestamp: now
        };
      }

      return result;
    } finally {
      pendingRequests.current.delete(cacheKey);
    }
  }, []);

  const clearCache = useCallback((key?: string) => {
    if (key) {
      delete requestCache[key];
    } else {
      Object.keys(requestCache).forEach(k => delete requestCache[k]);
    }
  }, []);

  const getCacheInfo = useCallback(() => {
    return {
      cacheSize: Object.keys(requestCache).length,
      cacheKeys: Object.keys(requestCache),
      pendingRequests: pendingRequests.current.size
    };
  }, []);

  return {
    makeRequest,
    clearCache,
    getCacheInfo
  };
}; 